import type * as React from 'react'
import { Tag, theme } from 'antd'
import { CarOutlined, ClockCircleOutlined, InfoCircleOutlined, ShopOutlined } from '@ant-design/icons'
import type { TreeNode, ProductHierarchy, LocationGroupData, TreeSelectTagProps } from './types'
import type { Origin } from '../../../../../../api/useFreight/useFreight'
import type { Destination } from '../../../../../../api/useFreight/useFreight'
import {
  type ThresholdFeeType,
  ThresholdFeeTypes,
  type TimeIntervalFeeType,
  TimeIntervalFeeTypes,
} from '@/api/useFreight/types'
import { Texto } from '@gravitate-js/excalibrr'

export const isThresholdFeeType = (type: string): boolean => {
  return ThresholdFeeTypes.includes(type as ThresholdFeeType)
}

export const isTimeIntervalFeeType = (type: string): boolean => {
  return TimeIntervalFeeTypes.includes(type as TimeIntervalFeeType)
}

export const isPercentageFeeType = (type: string): boolean => {
  return type?.includes('%') || false
}

export const getCascaderProductOptions = (productHierarchy: ProductHierarchy | undefined) => {
  if (!productHierarchy) return []
  return Object.entries(productHierarchy).map(([category, products]) => ({
    value: category,
    label: category,
    children: products.map((product) => ({
      value: product.id,
      label: product.name,
    })),
  }))
}

export const filterTreeNode = (input: string, node: TreeNode): boolean => {
  const title = node?.label ?? ''
  return String(title).toLowerCase().includes(input.toLowerCase())
}

export const getLocationIdsByState = (
  state: string,
  allLocations: Array<{ id: string; name: string; state?: string }> = []
): string[] => {
  return allLocations.filter((loc) => loc.state === state).map((loc) => loc.id)
}

export const getLocationIdsByRegion = (regionName: string, allLocations: Array<Origin | Destination>): string[] => {
  return allLocations.filter((loc) => loc.freight_region_name === regionName).map((loc) => loc.id)
}

export const getPrefillTreeSelectValues = (idsToPrefill: string[] = [], treeNodes: TreeNode[] = []): string[] => {
  const prefillValues = new Set<string>()
  const remainingIds = new Set(idsToPrefill)

  const checkNode = (node: TreeNode) => {
    if (!node) return

    if (
      'children' in node &&
      node.children &&
      node.children.length > 0 &&
      typeof node.value === 'string' &&
      node.value.includes('_')
    ) {
      const childIds = new Set(node.children.map((child) => child.value))
      let allChildrenFound = true
      let foundChildCount = 0

      for (const childId of childIds) {
        if (!remainingIds.has(childId)) {
          allChildrenFound = false
          break
        }
        foundChildCount++
      }

      if (allChildrenFound && foundChildCount === childIds.size && childIds.size > 0) {
        prefillValues.add(node.value)
        for (const childId of childIds) {
          remainingIds.delete(childId)
        }
        return
      }
    }

    if (remainingIds.has(node.value)) {
      prefillValues.add(node.value)
      remainingIds.delete(node.value)
    }

    if ('children' in node && node.children) {
      node.children.forEach(checkNode)
    }
  }

  treeNodes.forEach(checkNode)

  for (const id of remainingIds) {
    const existsInTree = (nodes: TreeNode[]): boolean => {
      for (const node of nodes) {
        if (node.value === id) return true
        if ('children' in node && node.children && existsInTree(node.children)) return true
      }
      return false
    }

    if (existsInTree(treeNodes)) {
      prefillValues.add(id)
    }
  }

  return Array.from(prefillValues)
}

export const calculatePrefillForTree = (
  structuredData: LocationGroupData | undefined,
  treeNodesForThisSelect: TreeNode[],
  allMetadataLocations: Array<Origin | Destination>,
  dataType: 'sites' | 'terminals'
): string[] => {
  if (!structuredData) return []

  const targetIndividualIds = new Set<string>()

  if (structuredData.locations) {
    for (const loc of structuredData.locations) {
      targetIndividualIds.add(loc.id)
    }
  }

  if (dataType === 'sites') {
    if (structuredData.site_states) {
      for (const state of structuredData.site_states) {
        for (const id of getLocationIdsByState(state, allMetadataLocations)) {
          targetIndividualIds.add(id)
        }
      }
    }
    if (structuredData.site_regions) {
      for (const region of structuredData.site_regions) {
        for (const id of getLocationIdsByRegion(region.name, allMetadataLocations)) {
          targetIndividualIds.add(id)
        }
      }
    }
  } else {
    if (structuredData.terminal_states) {
      for (const state of structuredData.terminal_states) {
        for (const id of getLocationIdsByState(state, allMetadataLocations)) {
          targetIndividualIds.add(id)
        }
      }
    }
  }

  const allTargetIdsArray = Array.from(targetIndividualIds)

  if (allTargetIdsArray.length === 0) {
    return []
  }

  return getPrefillTreeSelectValues(allTargetIdsArray, treeNodesForThisSelect)
}

export const tagRender = (props: TreeSelectTagProps) => {
  const { label: initialLabel, value, closable, onClose } = props

  const findNodeData = (searchValue: string, nodes: TreeNode[]): TreeNode | null => {
    for (const node of nodes) {
      if (node.value === searchValue) {
        return node
      }

      if ('children' in node && node.children) {
        const foundNode = findNodeData(searchValue, node.children)
        if (foundNode) return foundNode
      }
    }
    return null
  }

  // This would normally use the treeData from the component,
  // but for this implementation we'll just display the label
  let displayLabel = value as React.ReactNode
  if (initialLabel) {
    displayLabel = initialLabel
  }

  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  return (
    <Tag
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      style={{
        marginRight: 3,
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
      }}
    >
      {displayLabel}
    </Tag>
  )
}

export const getFeeTypeUnit = (type: string, interval?: number): string | React.ReactNode => {
  switch (type) {
    case 'Base Freight %':
    case 'Base & Accessorial %':
    case 'Total Freight %':
    case 'Base & Surcharge %':
    case 'Surcharge %':
      return '%'
    case 'Per Mile':
      return 'per mile'
    case 'Per Hour':
      return 'per hour'
    case 'Per Volume':
      return 'per gallon'
    case 'Per Order':
      return 'per order'
    case 'Per Stop':
      return 'per stop'
    case 'Per Drop':
      return 'per drop'
    case 'Per Load':
      return 'per load'
    case 'Per No Load':
      return 'per no load'
    case 'Detention':
    case 'Demurrage':
    case 'Demurrage and Detention':
      return (
        <>
          per <b>{interval} min</b>
        </>
      )
    case 'Out of Route':
      return 'per mile'
    case 'Per Retain':
      return 'per retain'
    case 'Short Load':
      return 'per load'
    default:
      return ''
  }
}

export const getThresholdUnit = (type: string): string => {
  switch (type) {
    case 'Per Stop':
      return 'stop(s)'
    case 'Per Drop':
      return 'drop(s)'
    case 'Per Load':
      return 'load(s)'
    case 'Per No Load':
      return 'no load(s)'
    case 'Detention':
    case 'Demurrage':
    case 'Demurrage and Detention':
      return 'min(s)'
    case 'Out of Route':
      return 'mile(s)'
    default:
      return ''
  }
}

export const getRuleIcon = (type: string) => {
  const { token } = theme.useToken()

  switch (type) {
    case 'Order Event':
      return <CarOutlined style={{ fontSize: '16px', color: token.colorPrimary }} />
    case 'Stop':
      return <ShopOutlined style={{ fontSize: '16px', color: token.colorSuccess }} />
    case 'Service Time':
      return <ClockCircleOutlined style={{ fontSize: '16px', color: token.colorWarning }} />
    default:
      return <InfoCircleOutlined />
  }
}
